import { useCallback } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { useIsFetching } from "@tanstack/react-query";

import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry
} from "geojson";

import { useGetAnalysisSummary } from "@/api/analysis/useAnalysisAPI";
import {
  CATCHMENT_ID_ATTRIBUTE,
  GRID_ID_ATTRIBUTE,
  POLYGON_ID_ATTRIBUTE
} from "@/components/analysisSummary/filterByGrid";
import NotificationContent from "@/components/notification/notificationContent";
import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import { MESSAGE } from "@/constants/message";
import type { RootState } from "@/store";
import {
  setGeneralSummary,
  setGridsSelected
} from "@/store/slice/analysisSummary";
import { EOutputAnalysisType } from "@/types/spatial";
import { getSourceProperties } from "@/utils/map";

import { useMap } from "../map/core/useMapContext";
import useToast, { EToastType } from "../ui/useToast";

const SUMMARY_FIT_ZOOM = 14;
const SUMMARY_OFFSET: [number, number] = [-150, 0];
const GENERATING_SUMMARY_TOAST_ID = "generating-summary-analysis-toast";

export const useAnalysisResultSummary = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { map, showOnlyLayer, handleMovePositionToLayer } = useMap();

  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);
  const { datasets } = useSelector((state: RootState) => state.dataset);

  // `latestAnalysisId` is the single source of truth — it's set when an
  // analysis runs and is guaranteed to exist before View Summary can be
  // clicked. Both fetchSummary and isFetching key off the same value, so
  // no extra Redux sync state is needed.
  const isFetching =
    useIsFetching({ queryKey: ["analysis-summary", latestAnalysisId] }) > 0;

  const { refetch: refetchAnalysisSummary } = useGetAnalysisSummary(
    latestAnalysisId ?? "",
    { enabled: false }
  );

  const highlightAnalysisResult = useCallback(() => {
    const analysisResultDataset = Object.keys(datasets)
      .map((key) => datasets[key])
      .find(
        (dataset) =>
          [
            EOutputAnalysisType.H3,
            EOutputAnalysisType.GEOHASH,
            EOutputAnalysisType.GRID,
            EOutputAnalysisType.PROFILING
          ].includes(
            dataset.data.spatial_aggregation_type as EOutputAnalysisType
          ) && dataset.data.total_rows > 0
      );
    const analysisResultDatasetId = analysisResultDataset?.data.id;

    showOnlyLayer(analysisResultDatasetId as string);

    const featureData = getSourceProperties(
      map,
      analysisResultDatasetId as string
    ) as FeatureCollection<Geometry, GeoJsonProperties>;

    const highestScoreFeature = featureData.features.reduce<
      Feature<Geometry, GeoJsonProperties>
    >(
      (acc, curr) => {
        return (curr?.properties?.["Score"] || 0) >
          (acc?.properties?.["Score"] || 0)
          ? curr
          : acc;
      },
      {} as Feature<Geometry, GeoJsonProperties>
    );

    dispatch(
      setGridsSelected([
        highestScoreFeature.properties?.[GRID_ID_ATTRIBUTE] ||
          highestScoreFeature.properties?.[CATCHMENT_ID_ATTRIBUTE] ||
          highestScoreFeature.properties?.[POLYGON_ID_ATTRIBUTE] ||
          DROPDOWN_ALL_VALUE
      ])
    );

    if (highestScoreFeature) {
      handleMovePositionToLayer(
        { type: "FeatureCollection", features: [highestScoreFeature] },
        { zoom: SUMMARY_FIT_ZOOM, offset: SUMMARY_OFFSET }
      );
    }
  }, [datasets, dispatch, handleMovePositionToLayer, map, showOnlyLayer]);

  const fetchSummary = useCallback(
    async (analysisId: string) => {
      if (!analysisId) return;

      showToast({
        id: GENERATING_SUMMARY_TOAST_ID,
        type: EToastType.LOADING,
        message: (
          <NotificationContent
            description={MESSAGE.ANALYSIS_SUMMARY.GENERATING_SUMMARY}
          />
        )
      });

      try {
        const { data, error } = await refetchAnalysisSummary();
        if (error || !data) throw error ?? new Error("No analysis summary");
        toast.dismiss(GENERATING_SUMMARY_TOAST_ID);
        dispatch(setGeneralSummary(data));
        highlightAnalysisResult();
      } catch {
        toast.dismiss(GENERATING_SUMMARY_TOAST_ID);
        showToast({
          type: EToastType.ERROR,
          message: (
            <NotificationContent
              description={MESSAGE.ANALYSIS_SUMMARY.GENERATING_SUMMARY_FAILED}
            />
          )
        });
      }
    },
    [dispatch, highlightAnalysisResult, refetchAnalysisSummary, showToast]
  );

  return { fetchSummary, isFetching };
};
